<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="internalOpen"
        class="ecx-drawer-backdrop fixed inset-0 bg-black bg-opacity-25 z-[9998]"
        @click="handleBackdropClick"
      />
    </Transition>

    <Transition
      :enter-active-class="enterActiveClass"
      :enter-from-class="enterFromClass"
      :enter-to-class="enterToClass"
      :leave-active-class="leaveActiveClass"
      :leave-from-class="leaveFromClass"
      :leave-to-class="leaveToClass"
    >
      <aside
        v-if="internalOpen"
        ref="drawerRef"
        :role="trapFocus ? 'dialog' : undefined"
        :aria-modal="trapFocus ? 'true' : undefined"
        :tabindex="trapFocus ? '-1' : undefined"
        :class="[
          'ecx-drawer fixed top-0 bg-white z-[9999] flex flex-col overflow-hidden',
          sideClass,
          sizeClass,
          roundedClass
        ]"
        @click.stop
        @keydown="handleFocusTrapKeydown"
      >
        <div v-if="showHeader" class="ecx-drawer-header flex-shrink-0">
          <div class="flex items-center justify-between self-stretch px-6 py-6">
            <div class="flex flex-1 items-center gap-4">
              <div
                v-if="$slots.icon || icon"
                class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border-2 border-white bg-royal-25 p-2 shadow-ecx-drawer-icon-ring"
              >
                <slot name="icon">
                  <component
                    v-if="icon"
                    :is="icon"
                    class="h-5 w-5"
                  />
                </slot>
              </div>
              <div class="min-w-0 flex-1">
                <h2 class="font-mont text-xl font-normal leading-4 tracking-drawer-title text-royal-950">
                  {{ title }}
                </h2>
                <p
                  v-if="subtitle"
                  class="mt-1 text-base text-form-description"
                >
                  {{ subtitle }}
                </p>
              </div>
            </div>
            <button
              v-if="showCloseButton"
              class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg p-2 transition-colors hover:bg-grey-50"
              aria-label="Fechar"
              @click="handleClose"
            >
              <svg
                class="h-5 w-5 text-form-label"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div class="border-b border-solid border-grey-100" />
        </div>

        <div class="ecx-drawer-content flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-visible ecx-drawer-scroll">
          <div :class="['flex min-h-0 w-full min-w-0 flex-1 flex-col', contentPaddingClass]">
            <slot />
          </div>
        </div>

        <div 
          v-if="$slots.footer" 
          class="ecx-drawer-footer flex flex-shrink-0 items-center border-t border-solid border-grey-100 bg-white px-6 py-6"
        >
          <slot name="footer" />
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, onUnmounted, ref, nextTick, type Component } from 'vue'

export type DrawerSide = 'right' | 'left'
export type DrawerSize = 'sm' | 'md' | 'md-fixed' | 'cashback' | 'lg' | 'xl' | 'full'

export interface DrawerProps {
  /** Controla se o drawer está aberto */
  open: boolean
  /** Título do drawer */
  title?: string
  /** Subtítulo opcional */
  subtitle?: string
  /** Componente de ícone */
  icon?: Component
  /** Lado do drawer */
  side?: DrawerSide
  /** Tamanho do drawer */
  size?: DrawerSize
  /** Mostra o header */
  showHeader?: boolean
  /** Mostra o botão de fechar */
  showCloseButton?: boolean
  /** Fecha ao clicar no backdrop */
  closeOnBackdrop?: boolean
  /** Fecha ao pressionar ESC */
  closeOnEsc?: boolean
  /** Bloqueia o scroll do body quando aberto */
  lockBodyScroll?: boolean
  /** Adiciona bordas arredondadas */
  rounded?: boolean
  /** Adiciona margin ao drawer */
  withMargin?: boolean
  /** Padding customizado para o conteúdo */
  contentPadding?: 'none' | 'sm' | 'md' | 'lg'
  /** Ativa comportamento de modal: role="dialog", aria-modal, focus trap e foco ao abrir/restaurar ao fechar */
  trapFocus?: boolean
}

const props = withDefaults(defineProps<DrawerProps>(), {
  side: 'right',
  size: 'lg',
  showHeader: true,
  showCloseButton: true,
  closeOnBackdrop: true,
  closeOnEsc: true,
  lockBodyScroll: true,
  rounded: true,
  withMargin: true,
  contentPadding: 'lg',
  trapFocus: false,
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:open', value: boolean): void
}>()

// Estado interno para controlar a animação
// Isso garante que a animação funcione mesmo quando o componente é montado já com open=true
const internalOpen = ref(false)

// Sincroniza o estado interno com a prop (immediate: true para montagem com open=true)
watch(() => props.open, (newValue) => {
  internalOpen.value = newValue
}, { immediate: true })

// Classes de animação baseadas no lado
const enterActiveClass = computed(() => 'transition-transform duration-300 ease-out')
const leaveActiveClass = computed(() => 'transition-transform duration-300 ease-in')

const enterFromClass = computed(() => 
  props.side === 'right' ? 'translate-x-full' : '-translate-x-full'
)
const enterToClass = computed(() => 'translate-x-0')
const leaveFromClass = computed(() => 'translate-x-0')
const leaveToClass = computed(() => 
  props.side === 'right' ? 'translate-x-full' : '-translate-x-full'
)

// Classes de posicionamento
const sideClass = computed(() => {
  const base = props.withMargin ? 'm-4' : ''
  return props.side === 'right' 
    ? `right-0 ${base}` 
    : `left-0 ${base}`
})

// Classes de tamanho
const sizeClass = computed(() => {
  const height = props.withMargin ? 'h-[calc(100vh-32px)]' : 'h-screen'
  const sizes: Record<DrawerSize, string> = {
    sm: `w-full max-w-sm ${height}`,
    md: `w-full max-w-md ${height}`,
    'md-fixed': `w-full max-w-ecx-drawer-md-fixed ${height}`,
    cashback: `w-full max-w-[500px] ${height}`,
    lg: `w-full max-w-[750px] ${height}`,
    xl: `w-full max-w-[900px] ${height}`,
    full: `w-full ${height}`,
  }
  return sizes[props.size]
})

// Classes de borda arredondada
const roundedClass = computed(() => 
  props.rounded ? 'rounded-2xl' : ''
)

// Classes de padding do conteúdo
const contentPaddingClass = computed(() => {
  const paddings: Record<string, string> = {
    none: '',
    sm: 'px-4 py-4 pb-16',
    md: 'px-6 py-6 pb-20',
    lg: 'px-6 py-6 pb-20',
  }
  return paddings[props.contentPadding]
})

const handleClose = () => {
  emit('close')
  emit('update:open', false)
}

const handleBackdropClick = () => {
  if (props.closeOnBackdrop) {
    handleClose()
  }
}

// Fecha ao pressionar ESC
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && internalOpen.value && props.closeOnEsc) {
    handleClose()
  }
}

// Previne scroll do body quando o drawer está aberto
watch(() => internalOpen.value, (isOpen) => {
  if (props.lockBodyScroll) {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }
  
  if (props.closeOnEsc) {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    } else {
      document.removeEventListener('keydown', handleEscape)
    }
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
  document.removeEventListener('keydown', handleEscape)
  restoreFocusToTrigger()
})

const drawerRef = ref<HTMLElement | null>(null)
let triggerElement: HTMLElement | null = null

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

function getFocusableElements(): HTMLElement[] {
  if (!drawerRef.value) return []
  return Array.from(drawerRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
}

async function focusFirstFocusableElement(): Promise<void> {
  await nextTick()
  await nextTick()
  const [first] = getFocusableElements()
  ;(first ?? drawerRef.value)?.focus()
}

function restoreFocusToTrigger(): void {
  triggerElement?.focus()
  triggerElement = null
}

const handleFocusTrapKeydown = (event: KeyboardEvent) => {
  if (!props.trapFocus) return
  if (event.key !== 'Tab') return
  const focusable = getFocusableElements()
  if (focusable.length === 0) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(() => internalOpen.value, (isOpen) => {
  if (!props.trapFocus) return
  if (isOpen) {
    triggerElement = document.activeElement as HTMLElement | null
    void focusFirstFocusableElement()
  } else {
    restoreFocusToTrigger()
  }
})
</script>

<style scoped>
.ecx-drawer-scroll::-webkit-scrollbar {
  width: 6px;
}

.ecx-drawer-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.ecx-drawer-scroll::-webkit-scrollbar-thumb {
  background: #F6F6F9;
  border-radius: 3px;
}

.ecx-drawer-scroll::-webkit-scrollbar-thumb:hover {
  background: #EEEFF1;
}

/* Firefox */
.ecx-drawer-scroll {
  scrollbar-width: thin;
  scrollbar-color: #F6F6F9 transparent;
}
</style>

